/**
 * App Settings Context
 * Provides app display settings to all components
 * Caches settings to avoid repeated API calls
 */

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { getAppDisplaySettings } from '../services/settingsService'

// Default display settings (show all tabs)
const DEFAULT_DISPLAY_SETTINGS = {
  showServicos: true,
  showProdutos: true,
  showAssinaturas: true,
}

const AppSettingsContext = createContext({
  displaySettings: DEFAULT_DISPLAY_SETTINGS,
  isLoading: true,
  refreshSettings: () => {},
})

export const AppSettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuthStore()
  const [displaySettings, setDisplaySettings] = useState(DEFAULT_DISPLAY_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)

  const fetchSettings = async () => {
    try {
      setIsLoading(true)
      const settings = await getAppDisplaySettings()
      setDisplaySettings(settings)
    } catch (error) {
      // Use defaults on error
      setDisplaySettings(DEFAULT_DISPLAY_SETTINGS)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch settings when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings()
    } else {
      // Reset to defaults when not authenticated
      setDisplaySettings(DEFAULT_DISPLAY_SETTINGS)
      setIsLoading(false)
    }
  }, [isAuthenticated])

  const refreshSettings = () => {
    if (isAuthenticated) {
      fetchSettings()
    }
  }

  return (
    <AppSettingsContext.Provider value={{ displaySettings, isLoading, refreshSettings }}>
      {children}
    </AppSettingsContext.Provider>
  )
}

/**
 * Hook to access app display settings
 * @returns {{ displaySettings: Object, isLoading: boolean, refreshSettings: Function }}
 */
export const useAppSettings = () => {
  const context = useContext(AppSettingsContext)
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider')
  }
  return context
}

export default AppSettingsContext
